import type { CryptoCurrencyDataSource, FiatCurrency } from '@0xtorch/core'
import {
  type AvailableApiCategory,
  FailedToAnalyzeExchangeException,
} from '@pkg/basic'
import {
  getAnalyzingTargetApiAccounts,
  selectCryptoCurrencies,
} from '@pkg/database-portfolio'
import {
  type AssetFetchClient,
  fetchSymbolAssetMap,
} from '@pkg/datasource-client'
import {
  type ApiConfig,
  type AsterApiType,
  type BackpackApiType,
  type BinanceApiType,
  type BinanceJapanApiType,
  type BitbankApiType,
  type BitgetApiType,
  type BybitApiType,
  type FetchApiData,
  type GateApiType,
  type HibachiApiType,
  type HyperliquidApiType,
  type MexcApiType,
  type SignAsterV3Payload,
  type SodexApiType,
  asterApiConfigs,
  backpackApiConfigs,
  binanceApiConfigs,
  binanceJapanApiConfigs,
  bitbankApiConfigs,
  bitgetApiConfigs,
  bybitApiConfigs,
  createAssetRecord,
  createAsterV3LocalSigner,
  gateApiConfigs,
  hibachiApiConfigs,
  hyperliquidApiConfigs,
  mexcApiConfigs,
  sodexApiConfigs,
} from '@pkg/exchange-api'
import type { OnUpdateApiProgressCallback, RunToDatabase } from '../types'
import {
  extractCryptoIdsFromSymbolAssetMap,
  fetchMissingCryptoCurrencies,
} from './fetchMissingCryptoCurrencies'
import { syncExchangeApiData } from './syncExchangeApiData'

type AnalyzeExchangeApiAccounts = (params: {
  accountIds: number[]
  assetFetchClient: AssetFetchClient
  category: AvailableApiCategory
  cryptoCurrencyApiEndpoint: string
  cryptoCurrencyDataSource: CryptoCurrencyDataSource
  fiat: FiatCurrency
  fiats: FiatCurrency[]
  portfolioStartTime: number
  proxy?: string
  getToken?: () => Promise<string | null>
  onError?: (error: unknown) => void
  onUpdateProgress?: OnUpdateApiProgressCallback
  readFromDatabase: RunToDatabase
  writeToDatabase: RunToDatabase
}) => Promise<void>

export const analyzeExchangeApiAccounts: AnalyzeExchangeApiAccounts = async ({
  accountIds,
  assetFetchClient,
  category,
  cryptoCurrencyApiEndpoint,
  cryptoCurrencyDataSource,
  fiat,
  fiats,
  portfolioStartTime,
  proxy,
  getToken,
  onError,
  onUpdateProgress,
  readFromDatabase,
  writeToDatabase,
}) => {
  try {
    // DB から API 認証情報と account 毎の API 情報取得
    // 対象 API が無い場合は処理終了
    const targetApiAccounts = await readFromDatabase((database) =>
      getAnalyzingTargetApiAccounts({
        database,
        accountIds,
      }),
    )
    const size = targetApiAccounts.reduce(
      (sum, account) => sum + account.apis.length,
      0,
    )
    if (size === 0) {
      return
    }

    // asset client から symbolAssetMap 取得
    const symbolAssetMap = await fetchSymbolAssetMap({
      assetFetchClient,
      category,
    })

    // symbolAssetMap から必要な crypto ID を抽出
    const requiredCryptoIds = extractCryptoIdsFromSymbolAssetMap(symbolAssetMap)

    // DB から crypto currency 一括取得
    const existingCryptoes = await readFromDatabase((database) =>
      selectCryptoCurrencies({ database }),
    )

    // 不足している crypto currency を API から取得してローカル DB に保存
    const cryptoes = await fetchMissingCryptoCurrencies({
      requiredCryptoIds,
      existingCryptoes,
      cryptoCurrencyApiEndpoint,
      writeToDatabase,
    })

    // cryptoes と fiats から AssetRecord 生成
    const assets = createAssetRecord({
      cryptoes,
      fiats,
    })

    // progress 更新
    if (onUpdateProgress) {
      onUpdateProgress({
        done: 0,
        size,
      })
    }

    // signer private key ごとに署名関数を cache
    const signerCache = new Map<string, SignAsterV3Payload>()
    const resolveSignAsterV3 = (params: {
      apiPassphrase?: string
      apiSecret?: string
    }): SignAsterV3Payload | undefined => {
      // Aster で apiPassphrase (signer address) が設定されているのが v3 アカウント。
      // 設定されていない場合は v1 (HMAC) アカウントなので、
      // apiSecret を private key として扱わない (HMAC secret を viem に渡すと crash する)。
      if (category !== 'aster' || !params.apiPassphrase || !params.apiSecret) {
        return undefined
      }
      const cached = signerCache.get(params.apiSecret)
      if (cached) {
        return cached
      }
      const signer = createAsterV3LocalSigner(params.apiSecret)
      signerCache.set(params.apiSecret, signer)
      return signer
    }

    let done = 0
    for (const account of targetApiAccounts) {
      const signAsterV3 = resolveSignAsterV3({
        apiPassphrase: account.apiPassphrase,
        apiSecret: account.apiSecret,
      })
      for (const api of account.apis) {
        // progress 更新
        if (onUpdateProgress) {
          onUpdateProgress({
            key: {
              type: api.type,
              subType: api.subType,
            },
            range: 0,
            synced: 0,
          })
          await new Promise((resolve) => setTimeout(resolve, 0)) // Yield to event loop to avoid blocking
        }

        // ApiConfig , fetchApiData の取得
        const config = getApiConfig({
          category,
          type: api.type,
        })
        const fetchApiData = await getFetchApiData({
          category,
          type: api.type,
        })

        // API データ同期
        await syncExchangeApiData({
          accountId: account.id,
          apiId: api.id,
          apiKey: account.apiKey,
          apiPassphrase: account.apiPassphrase,
          apiSecret: account.apiSecret,
          signAsterV3,
          assets,
          category,
          config,
          cryptoCurrencyDataSource,
          fiat,
          proxy,
          startTime: account.apiStartTime ?? portfolioStartTime,
          subType: api.subType,
          symbolAssetMap,
          fetchApiData,
          getToken,
          onError,
          onUpdateProgress,
          writeToDatabase,
        })

        // progress 更新
        done += 1
        if (onUpdateProgress) {
          onUpdateProgress({
            done,
          })
          await new Promise((resolve) => setTimeout(resolve, 0)) // Yield to event loop to avoid blocking
        }
      }
    }
  } catch (error) {
    throw new FailedToAnalyzeExchangeException(error, category)
  }
}

const getApiConfig = (params: {
  category: AvailableApiCategory
  type: string
}): ApiConfig => {
  const { category, type } = params

  switch (category) {
    case 'aster': {
      return asterApiConfigs[type as AsterApiType]
    }
    case 'backpack': {
      return backpackApiConfigs[type as BackpackApiType]
    }
    case 'binance': {
      return binanceApiConfigs[type as BinanceApiType]
    }
    case 'binance-japan': {
      return binanceJapanApiConfigs[type as BinanceJapanApiType]
    }
    case 'bitbank': {
      return bitbankApiConfigs[type as BitbankApiType]
    }
    case 'bitget': {
      return bitgetApiConfigs[type as BitgetApiType]
    }
    case 'bybit': {
      return bybitApiConfigs[type as BybitApiType]
    }
    case 'gate': {
      return gateApiConfigs[type as GateApiType]
    }
    case 'hibachi': {
      return hibachiApiConfigs[type as HibachiApiType]
    }
    case 'hyperliquid': {
      return hyperliquidApiConfigs[type as HyperliquidApiType]
    }
    case 'mexc': {
      return mexcApiConfigs[type as MexcApiType]
    }
    case 'sodex': {
      return sodexApiConfigs[type as SodexApiType]
    }
    default: {
      throw new Error(`Unsupported API category: ${category satisfies never}`)
    }
  }
}

const getFetchApiData = async (params: {
  category: AvailableApiCategory
  type: string
}): Promise<FetchApiData> => {
  const { category, type } = params

  switch (category) {
    case 'aster': {
      const asterType = type as AsterApiType
      const { fetchAsterApiDataFuncs } = await import(
        './fetchAsterApiDataFuncs'
      )
      return fetchAsterApiDataFuncs[asterType]
    }
    case 'backpack': {
      const backpackType = type as BackpackApiType
      const { fetchBackpackApiDataFuncs } = await import(
        './fetchBackpackApiDataFuncs'
      )
      return fetchBackpackApiDataFuncs[backpackType]
    }
    case 'binance': {
      const binanceType = type as BinanceApiType
      const { fetchBinanceApiDataFuncs } = await import(
        './fetchBinanceApiDataFuncs'
      )
      return fetchBinanceApiDataFuncs[binanceType]
    }
    case 'binance-japan': {
      const binanceJapanType = type as BinanceJapanApiType
      const { fetchBinanceJapanApiDataFuncs } = await import(
        './fetchBinanceJapanApiDataFuncs'
      )
      return fetchBinanceJapanApiDataFuncs[binanceJapanType]
    }
    case 'bitbank': {
      const bitbankType = type as BitbankApiType
      const { fetchBitbankApiDataFuncs } = await import(
        './fetchBitbankApiDataFuncs'
      )
      return fetchBitbankApiDataFuncs[bitbankType]
    }
    case 'bitget': {
      const bitgetType = type as BitgetApiType
      const { fetchBitgetApiDataFuncs } = await import(
        './fetchBitgetApiDataFuncs'
      )
      return fetchBitgetApiDataFuncs[bitgetType]
    }
    case 'bybit': {
      const bybitType = type as BybitApiType
      const { fetchBybitApiDataFuncs } = await import(
        './fetchBybitApiDataFuncs'
      )
      return fetchBybitApiDataFuncs[bybitType]
    }
    case 'gate': {
      const gateType = type as GateApiType
      const { fetchGateApiDataFuncs } = await import('./fetchGateApiDataFuncs')
      return fetchGateApiDataFuncs[gateType]
    }
    case 'hibachi': {
      const hibachiType = type as HibachiApiType
      const { fetchHibachiApiDataFuncs } = await import(
        './fetchHibachiApiDataFuncs'
      )
      return fetchHibachiApiDataFuncs[hibachiType]
    }
    case 'hyperliquid': {
      const hyperliquidType = type as HyperliquidApiType
      const { fetchHyperliquidApiDataFuncs } = await import(
        './fetchHyperliquidApiDataFuncs'
      )
      return fetchHyperliquidApiDataFuncs[hyperliquidType]
    }
    case 'mexc': {
      const mexcType = type as MexcApiType
      const { fetchMexcApiDataFuncs } = await import('./fetchMexcApiDataFuncs')
      return fetchMexcApiDataFuncs[mexcType]
    }
    case 'sodex': {
      const sodexType = type as SodexApiType
      const { fetchSodexApiDataFuncs } = await import(
        './fetchSodexApiDataFuncs'
      )
      return fetchSodexApiDataFuncs[sodexType]
    }
    default: {
      throw new Error(`Unsupported API category: ${category satisfies never}`)
    }
  }
}
